import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { MapPin, Plane, CreditCard, MessageCircle, Shield, Clock, Phone, Mail, Users, Instagram, Building2, ShoppingBag, Camera, Package, Map, Navigation, Play, ArrowRight, Car, DollarSign, Lock } from "lucide-react";

import getApiUrl from "@/utils/api";
import { WeatherSection } from "@/components/WeatherSection";
import { WhatsAppShareDialog } from "@/components/WhatsAppShareDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import ReviewsSection from "@/components/ReviewsSection";
import { useLanguage } from "@/i18n/LanguageContext";

const API = getApiUrl();

// Company logo URL
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_20e37a3a-e6c9-4384-b519-a00f19f82c1f/artifacts/f12i0vth_Screenshot_20260211_021213_Chrome.jpg";
const INSTAGRAM_QR = "https://customer-assets.emergentagent.com/job_20e37a3a-e6c9-4384-b519-a00f19f82c1f/artifacts/sl0mp273_Screenshot_20260210_194632_WhatsApp.jpg";

export default function LandingPage() {
  usePageTracker("Home");
  const { t } = useLanguage();
  const [destinations, setDestinations] = useState([]);
  const [specialTrips, setSpecialTrips] = useState(null);

  useEffect(() => {
    fetchDestinations();
    fetchSpecialTrips();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations`);
      setDestinations(response.data);
    } catch (error) {
      console.error("Failed to fetch destinations:", error);
    }
  };

  const fetchSpecialTrips = async () => {
    try {
      const response = await axios.get(`${API}/special-trips`);
      setSpecialTrips(response.data);
    } catch (error) {
      console.error("Failed to fetch special trips:", error);
    }
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50 border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={COMPANY_LOGO} 
              alt="Easy Transportation" 
              className="h-[60px] rounded-lg object-cover"
              style={{ width: '87px' }}
            />
            <div>
              <span className="text-xl font-extrabold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Easy Transportation
              </span>
              <p className="text-xs text-muted-foreground">Easy Ride, Exceptional Experience</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="flex items-center gap-1.5 text-green-700 font-semibold hover:text-green-900 transition-colors">
              <Lock className="w-4 h-4" />
              Admin Portal
            </Link>
            <Link to="/check-status" className="text-muted-foreground hover:text-foreground transition-colors">
              {t("nav.check_booking")}
            </Link>
            <a href="tel:8159148139" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <Phone className="w-4 h-4" />
              (815) 914-8139
            </a>
            <LanguageSwitcher />
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/admin" className="md:hidden flex items-center gap-1 text-green-700 font-semibold text-sm">
              <Lock className="w-3.5 h-3.5" />
              Admin
            </Link>
            <LanguageSwitcher className="md:hidden" />
            <Link to="/book">
              <Button 
                data-testid="book-now-header-btn"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 font-bold shadow-lg hover:shadow-xl"
              >
                {t("nav.book_now")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-32 md:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1631128869897-68e78bf4fb7e')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center text-white">
          <span className="inline-block mt-8 md:mt-0 px-3 py-1.5 md:px-4 md:py-2 bg-green-500/90 text-white rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6 animate-float">
            J1 Visa Student Transportation - Summer {new Date().getFullYear()}
          </span>
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight mb-6 hero-title"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Easy Transportation<br />
            <span className="text-green-400">Easy Ride, Exceptional Experience</span>
          </h1>
          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
            Reliable transportation from Chicago airports to Wisconsin Dells, Six Flags, and more. 
            Special deals for J1 students - just $3,200 JMD ($20 USD) deposit to book!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button 
                data-testid="book-trip-hero-btn"
                className="rounded-full bg-green-500 text-white hover:bg-green-600 h-14 px-10 text-lg font-bold shadow-xl hover:shadow-2xl w-full sm:w-auto"
              >
                <Plane className="mr-2 w-5 h-5" />
                Book Your Trip
              </Button>
            </Link>
            <Link to="/group-booking">
              <Button 
                data-testid="group-booking-hero-btn"
                className="rounded-full bg-yellow-500 text-black hover:bg-yellow-400 h-14 px-10 text-lg font-bold shadow-xl hover:shadow-2xl w-full sm:w-auto"
              >
                <Users className="mr-2 w-5 h-5" />
                Group Booking (5+)
              </Button>
            </Link>
            <Link to="/make-payment">
              <Button
                data-testid="make-payment-hero-btn"
                variant="outline"
                className="rounded-full border-2 border-white text-white hover:bg-white hover:text-green-700 h-14 px-10 text-lg font-bold shadow-xl hover:shadow-2xl w-full sm:w-auto bg-white/10 backdrop-blur"
              >
                <DollarSign className="mr-2 w-5 h-5" />
                Make Payment
              </Button>
            </Link>

          </div>

          {/* WhatsApp Share — pre-filled messages for posting to status/groups */}
          <div className="mt-6 flex justify-center">
            <WhatsAppShareDialog
              buttonClassName="rounded-full border-2 border-green-300 bg-white/95 text-green-700 hover:bg-green-50 hover:border-green-500 h-11 px-6 text-sm font-semibold shadow-md"
              buttonTestId="hero-wa-share-btn"
            />
          </div>
        </div>
      </section>

      {/* Weather */}
      <WeatherSection />

      {/* Pricing Section */}
      <section className="py-16 md:py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t("pricing.title")}
            </h2>
            <p className="text-gray-400 text-lg">{t("pricing.subtitle")}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* O'Hare / Milwaukee Return */}
            <Card className="bg-gray-800 border-green-500/30 border-2 rounded-2xl overflow-hidden">
              <div className="bg-green-500 p-4">
                <h3 className="text-lg font-bold text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Return — ORD / MKE
                </h3>
              </div>
              <CardContent className="p-5 text-white">
                <ul className="space-y-3">
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Shared (2+ pax)</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">$85</span>
                  </li>
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      <span>Solo / Private</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">$200</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>With T&M Shipping</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">Bags travel separately</p>
                    </div>
                    <span className="text-2xl font-bold text-yellow-400">$70</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Midway Return */}
            <Card className="bg-gray-800 border-blue-500/30 border-2 rounded-2xl overflow-hidden">
              <div className="bg-blue-500 p-4">
                <h3 className="text-lg font-bold text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Return — MDW (Midway)
                </h3>
              </div>
              <CardContent className="p-5 text-white">
                <ul className="space-y-3">
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Shared (2+ pax)</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">$85</span>
                  </li>
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      <span>Solo / Private</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">$200</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span>With T&M Shipping</span>
                    </div>
                    <span className="text-2xl font-bold text-yellow-400">$70</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Madison Return */}
            <Card className="bg-gray-800 border-green-500/30 border-2 rounded-2xl overflow-hidden">
              <div className="bg-green-500 p-4">
                <h3 className="text-lg font-bold text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Return — MSN (Madison)
                </h3>
              </div>
              <CardContent className="p-5 text-white">
                <ul className="space-y-3">
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Shared (2+ pax)</span>
                    </div>
                    <span className="text-2xl font-bold text-green-400">$65</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      <span>Solo / Private</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-400">$80</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Baggage & Deposit */}
            <Card className="bg-gray-800 border-yellow-500/50 border-2 rounded-2xl overflow-hidden relative">
              <div className="absolute top-2 right-2 bg-yellow-500 text-black px-3 py-1 rounded-full text-sm font-bold">
                {t("pricing.special_deal")}
              </div>
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-4">
                <h3 className="text-lg font-bold text-black text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Baggage & Extras
                </h3>
              </div>
              <CardContent className="p-5 text-white">
                <ul className="space-y-3">
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <span>1st checked bag + Carry-on</span>
                    <span className="text-2xl font-bold text-green-400">{t("pricing.free")}</span>
                  </li>
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <span>Extra suitcase (each)</span>
                    <span className="text-2xl font-bold text-yellow-400">$5</span>
                  </li>
                  <li className="flex justify-between items-center border-b border-gray-700 pb-3">
                    <span>Deposit to book</span>
                    <span className="text-2xl font-bold text-green-400">$20</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span>Balance due at pickup</span>
                    <span className="text-sm font-bold text-gray-300">Remaining fare</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Notes */}
          <div className="mt-6 max-w-2xl mx-auto space-y-3">
            <div className="p-4 bg-yellow-600/20 border border-yellow-500/30 rounded-2xl text-sm text-center text-white">
              {t("pricing.waterpark_note")} <a href="tel:8159148139" className="text-green-400 font-bold">{t("pricing.contact_us")}</a>
            </div>
          </div>

          {/* Pricing Guidelines */}
          <div className="mt-10 max-w-3xl mx-auto" data-testid="pricing-guidelines-landing">
            <h4 className="text-xl font-bold text-white mb-5 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t("pricing.guidelines_title")}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex gap-3 items-start bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <span className="mt-1 w-2 h-2 rounded-full bg-green-400 shrink-0"></span>
                <p className="text-sm text-gray-300">{t("pricing.guidelines_deposit")}</p>
              </div>
              <div className="flex gap-3 items-start bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <span className="mt-1 w-2 h-2 rounded-full bg-green-400 shrink-0"></span>
                <p className="text-sm text-gray-300">{t("pricing.guidelines_shared")}</p>
              </div>
              <div className="flex gap-3 items-start bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <span className="mt-1 w-2 h-2 rounded-full bg-yellow-400 shrink-0"></span>
                <p className="text-sm text-gray-300">{t("pricing.guidelines_solo")}</p>
              </div>
              <div className="flex gap-3 items-start bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <span className="mt-1 w-2 h-2 rounded-full bg-green-400 shrink-0"></span>
                <p className="text-sm text-gray-300">{t("pricing.guidelines_suitcase")}</p>
              </div>
              <div className="sm:col-span-2 flex gap-3 items-start bg-gray-800/60 border border-gray-700 rounded-xl p-4">
                <span className="mt-1 w-2 h-2 rounded-full bg-blue-400 shrink-0"></span>
                <p className="text-sm text-gray-300">{t("pricing.guidelines_payment")}</p>
              </div>
            </div>
          </div>

          {/* Requirements */}
          <div className="mt-12 bg-green-500/20 border border-green-500/50 rounded-2xl p-6 max-w-3xl mx-auto">
            <h4 className="text-xl font-bold text-green-400 mb-4 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t("requirements.title")}
            </h4>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                <span>{t("requirements.pickup")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                <span>{t("requirements.deposit")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                <span>{t("requirements.seamless")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></span>
                <span>{t("requirements.final_payment")}</span>
              </li>
            </ul>
          </div>

          {/* Special Request Ride */}
          <div className="mt-8 bg-purple-500/20 border border-purple-500/50 rounded-2xl p-6 max-w-3xl mx-auto text-center">
            <h4 className="text-xl font-bold text-purple-300 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t("special.title")}
            </h4>
            <p className="text-gray-300 text-sm mb-4">{t("special.description")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:8159148139">
                <Button className="bg-purple-600 hover:bg-purple-700 rounded-xl px-6">
                  <Phone className="w-4 h-4 mr-2" />
                  {t("special.call")} (815) 914-8139
                </Button>
              </a>
              <a href="https://wa.me/18159148139?text=Hi!%20I%20need%20a%20ride%20to%20a%20destination%20not%20listed%20on%20your%20website." target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-purple-400 text-purple-300 hover:bg-purple-500/20 rounded-xl px-6">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t("special.whatsapp")}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t("features.title")}
            </h2>
            <p className="text-muted-foreground text-lg">{t("features.subtitle")}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-green-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("features.easy_booking")}</h3>
                <p className="text-muted-foreground">{t("features.easy_booking_desc")}</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-yellow-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("features.safe")}</h3>
                <p className="text-muted-foreground">{t("features.safe_desc")}</p>
              </CardContent>
            </Card>
            
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("features.flexible")}</h3>
                <p className="text-muted-foreground">{t("features.flexible_desc")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Reviews / Social Proof */}
      <ReviewsSection />

      {/* Video Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full text-sm font-semibold mb-4">
              <Play className="w-4 h-4" />
              See Us In Action
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Journey Starts Here
            </h2>
            <p className="text-gray-300 text-lg">Watch J1 students from Jamaica & Dominican Republic begin their adventure</p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-green-500/30">
            <video 
              className="w-full aspect-video object-cover"
              controls
              poster="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1280"
              playsInline
            >
              <source src="/easy_transport_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div className="mt-8 text-center">
            <Link to="/book">
              <Button size="lg" className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg rounded-full">
                Book Your Ride Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Airport Maps Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold mb-4">
              <Map className="w-4 h-4" />
              Airport Pickup Guide
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Find Your Pickup Location
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Click on your airport below for GPS directions to the pickup location
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5" />
                  ORD - O'Hare
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex flex-col flex-1">
                <div className="text-sm text-muted-foreground mb-3 space-y-1 flex-1">
                  <p className="font-medium text-foreground">Pickup Locations:</p>
                  <p>Terminal 1: Door 1D</p>
                  <p>Terminal 2: Door 2D</p>
                  <p>Terminal 3: Door 3D</p>
                  <p>Terminal 5: Door 5D</p>
                </div>
                <a href="https://www.google.com/maps/dir/?api=1&destination=41.9742,-87.9073&travelmode=walking" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-lg">
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Pickup
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5" />
                  MDW - Midway
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex flex-col flex-1">
                <p className="text-sm text-muted-foreground mb-3 flex-1">Door 3 - Ground Transportation</p>
                <a href="https://www.google.com/maps/dir/?api=1&destination=41.7859,-87.7524&travelmode=walking" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-lg">
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Pickup
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5" />
                  MSN - Madison
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex flex-col flex-1">
                <p className="text-sm text-muted-foreground mb-3 flex-1">Curbside - Outside Baggage Claim</p>
                <a href="https://www.google.com/maps/dir/?api=1&destination=43.1397,-89.3371&travelmode=walking" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-lg">
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Pickup
                  </Button>
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Plane className="w-5 h-5" />
                  MKE - Milwaukee
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 flex flex-col flex-1">
                <p className="text-sm text-muted-foreground mb-3 flex-1">Lower Level - Door C/D</p>
                <a href="https://www.google.com/maps/dir/?api=1&destination=42.9469,-87.8961&travelmode=walking" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-green-600 hover:bg-green-700 rounded-lg">
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Pickup
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              <Phone className="w-4 h-4 inline mr-2" />
              Can't find us? Call <a href="tel:8159148139" className="text-green-600 font-bold">(815) 914-8139</a>
            </p>
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Popular Destinations
            </h2>
            <p className="text-muted-foreground text-lg">Where J1 students work and play</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="destinations-grid">
            {destinations.map((dest) => (
              <Card 
                key={dest.id}
                className="destination-card group relative h-80 rounded-3xl overflow-hidden cursor-pointer border-0 shadow-lg"
                data-testid={`destination-card-${dest.id}`}
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${dest.image_url || "https://images.unsplash.com/photo-1590058572286-ccc2665c502e"}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium opacity-90">Wisconsin</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{dest.name}</h3>
                  <p className="text-white/80 text-sm">{dest.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Special Trips Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold mb-4">
              <MapPin className="w-4 h-4" />
              Special Trips Available!
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              More Than Just Airport Rides
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              We offer special trips to government offices, banks, shopping, and sightseeing destinations!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Government Services */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Government Services</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Social Security Office - Portage, WI</li>
                  <li>• Secretary of State - Portage, WI</li>
                  <li>• Secretary of State - Rockford, IL</li>
                </ul>
              </CardContent>
            </Card>

            {/* Shopping */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <ShoppingBag className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Shopping</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Shopping Malls - Madison</li>
                  <li>• Magnificent Mile - Chicago</li>
                  <li>• Apple Store - Chicago</li>
                </ul>
              </CardContent>
            </Card>

            {/* Sightseeing */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-lg mb-2">Sightseeing</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Downtown Chicago Tours</li>
                  <li>• Millennium Park & The Bean</li>
                  <li>• Navy Pier</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-muted-foreground mb-4">
              Need a special trip? Contact us to arrange your ride!
            </p>
            <a href="tel:8159148139">
              <Button className="rounded-full bg-blue-600 text-white hover:bg-blue-700 h-12 px-8">
                <Phone className="mr-2 w-4 h-4" />
                Call (815) 914-8139
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp Bot Section */}
      <section className="py-16 md:py-20 bg-green-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full text-sm font-semibold mb-6">
            <MessageCircle className="w-4 h-4" />
            WhatsApp Bot Available!
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Chat With Us on WhatsApp
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Message our WhatsApp bot anytime to check your booking status, get pricing info, or ask questions!
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <p className="font-bold text-green-600">STATUS</p>
              <p className="text-xs text-muted-foreground">Check booking</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <p className="font-bold text-green-600">PRICES</p>
              <p className="text-xs text-muted-foreground">View pricing</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <p className="font-bold text-green-600">BOOK</p>
              <p className="text-xs text-muted-foreground">Reserve trip</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <p className="font-bold text-green-600">HELP</p>
              <p className="text-xs text-muted-foreground">All commands</p>
            </div>
          </div>
          
          <a href="https://wa.me/18159148139" target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full bg-green-600 text-white hover:bg-green-700 h-14 px-10 text-lg font-bold">
              <MessageCircle className="mr-2 w-5 h-5" />
              Message Us on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* T&M Courier Shipping Only Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-green-50 to-teal-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm font-semibold mb-6">
            <Package className="w-4 h-4" />
            Shipping Only!
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Not Traveling With Us?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            You can still ship your items with T&M Courier Services! Ship barrels, suitcases, scooters, bicycles, TVs, and more - no transportation booking required.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-6">
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <span className="text-3xl">🧳</span>
              <p className="text-sm font-medium mt-2">Suitcases</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-2 border-green-200">
              <span className="text-3xl">🛢️</span>
              <p className="text-sm font-medium mt-2">Barrels</p>
              <p className="text-green-600 font-bold">$200</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-2 border-green-200">
              <span className="text-3xl">📦</span>
              <p className="text-sm font-medium mt-2">Boxes</p>
              <p className="text-green-600 font-bold">$350</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm">
              <span className="text-3xl">📺</span>
              <p className="text-sm font-medium mt-2">TVs & More</p>
            </div>
          </div>
          
          {/* Scooter Announcement */}
          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300 rounded-2xl p-4 mb-8 max-w-xl mx-auto">
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl">🛵</span>
              <div className="text-left">
                <p className="font-bold text-yellow-800 text-lg">We have 15 Scooters available!</p>
                <p className="text-yellow-700 text-sm">Buy or Rent - Contact us for details</p>
              </div>
            </div>
          </div>
          
          <Link to="/shipping">
            <Button className="rounded-full bg-green-600 text-white hover:bg-green-700 h-14 px-10 text-lg font-bold" data-testid="shipping-only-btn">
              <Package className="mr-2 w-5 h-5" />
              Request Shipping Only - Click Here
            </Button>
          </Link>
          
          <p className="text-sm text-muted-foreground mt-4">
            Contact: (815) 914-8139 | Tmcourierservice1@gmail.com
          </p>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-semibold mb-6">
            <Instagram className="w-4 h-4" />
            Follow Us on Instagram!
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Connect With Us
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Click the link or scan the QR code to follow us on Instagram for updates, photos, and J1 community news!
          </p>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              <img 
                src={INSTAGRAM_QR}
                alt="Instagram QR Code - @DELLSJ1S" 
                className="w-48 h-48 object-contain rounded-lg"
              />
              <p className="mt-3 text-sm font-bold text-gray-700">@DELLSJ1S</p>
            </div>
            
            <div className="text-left max-w-sm">
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Why Follow Us?
              </h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  Trip photos and updates
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                  J1 community events
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                  Special deals and promotions
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-pink-500 rounded-full"></span>
                  Wisconsin Dells highlights
                </li>
              </ul>
              
              <a 
                href="https://www.instagram.com/DELLSJ1S" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-6"
              >
                <Button className="rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 h-12 px-8 font-bold">
                  <Instagram className="mr-2 w-5 h-5" />
                  Follow @DELLSJ1S
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-green-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-60 h-60 bg-white rounded-full"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Book your transportation now and focus on what matters - your summer adventure!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book">
              <Button 
                data-testid="book-now-cta-btn"
                className="rounded-full bg-white text-green-600 hover:bg-white/90 h-14 px-12 text-lg font-bold shadow-xl"
              >
                Book Your Trip Now
              </Button>
            </Link>
            <a href="tel:8159148139">
              <Button 
                variant="outline"
                className="rounded-full border-2 border-white text-white hover:bg-white/10 h-14 px-12 text-lg font-bold"
              >
                <Phone className="mr-2 w-5 h-5" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src={COMPANY_LOGO}
                  alt="Easy Transportation"
                  className="h-[49px] rounded-lg object-cover"
                  style={{ width: '72px' }}
                />
                <div>
                  <span className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Easy Transportation</span>
                  <p className="text-xs text-gray-400">Easy Ride, Exceptional Experience</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Trusted transportation for J1 Visa students.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/book" className="hover:text-white">Book a Trip</Link></li>
                <li><Link to="/group-booking" className="hover:text-white">Group Booking (5+)</Link></li>
                <li><Link to="/check-status" className="hover:text-white">Check Status</Link></li>
                <li><Link to="/admin" className="hover:text-white">Admin Portal</Link></li>
                <li><Link to="/driver" className="hover:text-white">Driver App</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Destinations</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Wisconsin Dells</li>
                <li>Six Flags Great America</li>
                <li>Three Bears Water Park</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact Us</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-green-500" />
                  <a href="tel:8159148139" className="hover:text-white">(815) 914-8139</a>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-green-500" />
                  <a href="mailto:Miketransportsj1@gmail.com" className="hover:text-white">Miketransportsj1@gmail.com</a>
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-500" />
                  <a href="https://wa.me/18159148139" target="_blank" rel="noopener noreferrer" className="hover:text-white">WhatsApp Support</a>
                </li>
                <li className="flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  <a href="https://www.instagram.com/DELLSJ1S" target="_blank" rel="noopener noreferrer" className="hover:text-white">@DELLSJ1S</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>© {new Date().getFullYear()} Easy Transportation. All rights reserved.</p>
            <Link to="/privacy" className="text-gray-500 hover:text-white mt-1 inline-block">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
